/** RFC 5321 length cap on the entire email address. */
export const EMAIL_MAX_LENGTH = 254

/** RFC 1035: the domain part (everything after the last @) is capped at 253 chars. */
const DOMAIN_MAX_LENGTH = 253

/** RFC 1034 §3.1: each dot-separated DNS label is capped at 63 chars. */
const DNS_LABEL_MAX_LENGTH = 63

/**
 * Strict email format pattern. Originally ported from the backend
 * implementation in supabase/functions/organization-invitations/index.ts, then
 * tightened under DCP-151.
 * Rejects: missing @, missing TLD, consecutive dots, leading/trailing dots in
 * either local or domain part, embedded whitespace, and — added in DCP-151 —
 * domain labels that begin or end with a hyphen (e.g. `user@-x.com`,
 * `user@x-.com`), which RFC 1034/1123 forbid.
 * Deliberately stricter than Zod's permissive `.email()` — see DCP-151 for the
 * centralization rationale. Per-label / per-domain LENGTH limits are not
 * expressible in the regex and are enforced separately in isValidEmail.
 *
 * Structure:
 *   local  = [^\s@.]+(?:\.[^\s@.]+)*                 (dot-separated, no edge dots)
 *   label  = [^\s@.\-](?:[^\s@.]*[^\s@.\-])?         (no leading/trailing hyphen)
 *   domain = label(?:\.label)+                       (at least two labels → TLD)
 * The literal `.` between labels is a hard anchor (label chars exclude `.`),
 * so the nested quantifiers cannot redistribute characters across labels —
 * no catastrophic-backtracking risk.
 */
// Module-private: the only public entry point for format checks is isValidEmail
// (and the validateEmail/parseEmail/emailSchema constructors that wrap it).
// Not exported, so external code can't test against the raw pattern and bypass
// normalization.
const EMAIL_PATTERN =
  /^[^\s@.]+(?:\.[^\s@.]+)*@[^\s@.\-](?:[^\s@.]*[^\s@.\-])?(?:\.[^\s@.\-](?:[^\s@.]*[^\s@.\-])?)+$/

/**
 * Branded type guaranteeing the value has passed normalization + format check.
 * A function declaring `email: Email` cannot be invoked with a raw string —
 * the caller must go through validateEmail / parseEmail / emailSchema.
 * Runtime cost is zero — the brand erases at compile time.
 *
 * Uses a string-literal brand (not a module-local `unique symbol`): the symbol
 * variant can't be named across package boundaries, so consumers like
 * @dcp/tasks hit TS4023 on any exported schema whose inferred output is Email.
 */
export type Email = string & { readonly __emailBrand: 'Email' }

/**
 * Discriminated error codes for parseEmail. Stable identifiers that callers
 * can map to UI copy or i18n keys without depending on free-text messages.
 */
export type ParseEmailErrorCode = 'empty' | 'too_long' | 'invalid_format'

/**
 * Canonicalize an email string: strip surrounding whitespace and lowercase the
 * entire address. Does NOT validate format — pair with isValidEmail or use
 * validateEmail / parseEmail for the combined operation.
 *
 * @param raw - The raw input string from the user or external source.
 * @returns The trimmed, lowercased string.
 */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase()
}

/**
 * Check whether a string is structurally valid as an email address.
 * ASSUMES the input has already been normalized (i.e., passed through
 * normalizeEmail). Callers handing raw user input should use validateEmail
 * or parseEmail instead.
 *
 * @param email - A normalized (trimmed, lowercased) email string.
 * @returns True if the string satisfies length and format constraints.
 */
export function isValidEmail(email: string): boolean {
  if (email.length === 0 || email.length > EMAIL_MAX_LENGTH) return false
  if (!EMAIL_PATTERN.test(email)) return false
  // RFC 1034/1035 length limits the regex can't express: the domain part must
  // be ≤ 253 chars and each dot-separated label ≤ 63 chars. The regex already
  // guarantees a well-formed domain (≥ 2 non-empty labels, no edge hyphens),
  // so a single lastIndexOf('@') split is safe here.
  const domain = email.slice(email.lastIndexOf('@') + 1)
  if (domain.length > DOMAIN_MAX_LENGTH) return false
  return domain.split('.').every((label) => label.length <= DNS_LABEL_MAX_LENGTH)
}

/**
 * Normalize + validate. Returns the branded Email value on success.
 * Throws Error('Invalid email format') on any failure. Suitable for use as
 * a drop-in replacement for the legacy normalizeAndValidateEmail in the
 * organization-invitations edge function (preserves throw-Error contract).
 * Does NOT echo the raw input in the error message — keeps potentially
 * sensitive paste-mistakes out of logs and HTTP responses.
 *
 * @param raw - The raw email input, possibly with surrounding whitespace or mixed case.
 * @returns The branded Email value (normalized).
 * @throws {Error} With message 'Invalid email format' when empty, too long, or malformed.
 */
export function validateEmail(raw: string): Email {
  const result = parseEmail(raw)
  if (!result.ok) {
    throw new Error('Invalid email format')
  }
  return result.value
}

/**
 * Result-style normalize + validate. Never throws.
 * Returns a discriminated union: success holds the branded Email, failure
 * holds a stable error code (no raw input — same echo-guard rationale as
 * validateEmail).
 *
 * @param raw - The raw email input from any source.
 * @returns `{ ok: true, value: Email }` on success, or `{ ok: false, code: ParseEmailErrorCode }` on failure.
 */
export function parseEmail(
  raw: string,
): { ok: true; value: Email } | { ok: false; code: ParseEmailErrorCode } {
  const normalized = normalizeEmail(raw)
  if (normalized.length === 0) return { ok: false, code: 'empty' }
  if (normalized.length > EMAIL_MAX_LENGTH) return { ok: false, code: 'too_long' }
  // Delegate the format + domain-length checks to isValidEmail so the two
  // entry points (boolean isValidEmail vs. coded parseEmail) can never diverge
  // — one source of truth for "structurally valid".
  if (!isValidEmail(normalized)) return { ok: false, code: 'invalid_format' }
  return { ok: true, value: normalized as Email }
}
